/**
 * Naming blocking lots in a bulk refusal ("Lot 7, Lot 12, Lot 19 and 2 more"),
 * shared by Edit lots (BL-2146) and bulk Add contact (BUG-2127), so a refusal
 * names the lots the same way on every bulk save (ADR 026 — Server
 * Orchestration). Pure and client-safe (ADR 010 — Isomorphic Services).
 */

/** How many lots a refusal names before it counts the rest. */
export const MAX_NAMED_LOTS = 3;

/**
 * Join phrases with commas and a final "and": "a", "a and b", "a, b and c".
 *
 * @param parts The phrases.
 * @returns The joined phrase.
 * @sideEffect none — pure.
 */
export function joinWithAnd(parts: readonly string[]): string {
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * Name lots by their printed numbers: up to {@link MAX_NAMED_LOTS} directly,
 * the rest (and any unnumbered lots) as "and N more".
 *
 * @param opts.lotNumbers The lots' printed numbers, in the order to name them.
 * @param opts.unnamedCount Further lots with no number to print. Default 0.
 * @returns The phrase ("Lot 37", "Lot 12 and Lot 37", "Lot 7, Lot 12, Lot 19 and 2 more"), or null when no lot has a number.
 * @sideEffect none — pure.
 */
export function nameLots(opts: { lotNumbers: readonly string[]; unnamedCount?: number }): string | null {
  const { lotNumbers, unnamedCount = 0 } = opts;
  if (lotNumbers.length === 0) return null;
  const shown = lotNumbers.slice(0, MAX_NAMED_LOTS).map((n) => `Lot ${n}`);
  const remaining = lotNumbers.length - shown.length + unnamedCount;
  return remaining > 0 ? `${shown.join(', ')} and ${remaining} more` : joinWithAnd(shown);
}
